import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  InputAdornment,
  Alert,
  Skeleton,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as TemplateIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useProjectTemplateStore from '../../stores/projectTemplateStore';
import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';

const PROJECT_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'development', label: 'Development' },
  { value: 'content', label: 'Content' },
  { value: 'custom', label: 'Custom' }
];

const ProjectCreationOptions = ({ open, onClose, onCreateFromScratch, onCreateFromTemplate, onDuplicateProject }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const { hasPermission } = useAuthStore();
  const canViewTemplates = hasPermission('project_templates', 'read');

  const {
    templates,
    isLoading: templatesLoading,
    error: templatesError,
    fetchTemplates
  } = useProjectTemplateStore();

  const {
    projects,
    isLoading: projectsLoading,
    error: projectsError,
    fetchProjects
  } = useProjectStore();

  useEffect(() => {
    if (open) {
      fetchTemplates({ limit: 20 });
      fetchProjects({ limit: 20 });
    }
  }, [open, fetchTemplates, fetchProjects]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleCreateFromScratch = () => {
    onCreateFromScratch();
    handleClose();
  };

  const handleTemplateSelect = (template) => {
    onCreateFromTemplate(template);
    handleClose();
  };

  const handleProjectSelect = (project) => {
    onDuplicateProject(project);
    handleClose();
  };

  const handleClose = () => {
    setSelectedOption(null);
    setSearchTerm('');
    setSelectedCategory('all');
    onClose();
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchTerm || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const filteredProjects = projects.filter(project => {
    return !searchTerm || 
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const TemplateCard = ({ template }) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)'
        }
      }}
      onClick={() => handleTemplateSelect(template)}
    >
      <CardContent>
        <Typography variant="h6" noWrap gutterBottom>
          {template.name}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {template.description || 'No description provided'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={template.category} 
            size="small" 
            color="primary"
            variant="outlined"
          />
          <Chip 
            label={`${template.sections?.length || 0} sections`} 
            size="small"
            variant="outlined"
          />
          <Chip 
            label={`Used ${template.usageCount || 0} times`} 
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
      </CardContent>
    </Card>
  );

  const ProjectCard = ({ project }) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)'
        }
      }}
      onClick={() => handleProjectSelect(project)}
    >
      <CardContent>
        <Typography variant="h6" noWrap gutterBottom>
          {project.name}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {project.description || 'No description provided'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={project.type?.replace(/_/g, ' ')} 
            size="small" 
            color="primary"
            variant="outlined"
          />
          <Chip 
            label={project.status} 
            size="small"
            variant="outlined"
          />
          <Chip 
            label={project.clientId?.name || 'No Client'} 
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
      </CardContent>
    </Card>
  );

  const LoadingGrid = () => (
    <Grid container spacing={2}>
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="80%" height={32} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" height={20} />
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Skeleton variant="rectangular" width={80} height={24} />
                <Skeleton variant="rectangular" width={100} height={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md"
      fullWidth
    >
      {!selectedOption && (
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              How would you like to create your project?
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent sx={{ p: 3 }}>
        {!selectedOption ? (
          // Option Selection
          <List sx={{ pt: 0 }}>
            <ListItem 
              button
              onClick={() => handleOptionSelect('scratch')}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 2,
                p: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
            >
              <Box sx={{ 
                width: 56, 
                height: 56, 
                borderRadius: '50%',
                backgroundColor: 'primary.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3
              }}>
                <AddIcon sx={{ color: 'primary.main' }} />
              </Box>
              <ListItemText
                primary="Start from Scratch"
                secondary="Create an empty project and build it from the ground up"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>

            <ListItem 
              button
              onClick={() => canViewTemplates ? handleOptionSelect('template') : null}
              disabled={!canViewTemplates}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 2,
                p: 2,
                opacity: canViewTemplates ? 1 : 0.6,
                '&:hover': canViewTemplates ? {
                  backgroundColor: 'action.hover',
                  borderColor: 'secondary.main'
                } : {}
              }}
            >
              <Box sx={{ 
                width: 56, 
                height: 56, 
                borderRadius: '50%',
                backgroundColor: canViewTemplates ? 'secondary.50' : 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3
              }}>
                <TemplateIcon sx={{ color: canViewTemplates ? 'secondary.main' : 'text.disabled' }} />
              </Box>
              <ListItemText
                primary="Use a Template"
                secondary={canViewTemplates ? 
                  "Start with a pre-configured project template" : 
                  "You need permission to access templates"
                }
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>

            <ListItem 
              button
              onClick={() => handleOptionSelect('duplicate')}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'info.main'
                }
              }}
            >
              <Box sx={{ 
                width: 56, 
                height: 56, 
                borderRadius: '50%',
                backgroundColor: 'info.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3
              }}>
                <CopyIcon sx={{ color: 'info.main' }} />
              </Box>
              <ListItemText
                primary="Duplicate Existing Project"
                secondary="Copy the structure from one of your existing projects"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>
          </List>
        ) : selectedOption === 'scratch' ? (
          // Confirm scratch creation  
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AddIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Create Empty Project
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You'll start with a blank project and can add sections and tasks as needed.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleCreateFromScratch}
            >
              Continue
            </Button>
          </Box>
        ) : selectedOption === 'template' ? (
          // Template selection
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose a Template
            </Typography>
            
            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {PROJECT_CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Error Alert */}
            {templatesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {templatesError}
              </Alert>
            )}

            {/* Templates Grid */}
            {templatesLoading ? (
              <LoadingGrid />
            ) : filteredTemplates.length > 0 ? (
              <Grid container spacing={2}>
                {filteredTemplates.map((template) => (
                  <Grid item xs={12} sm={6} md={4} key={template._id}>
                    <TemplateCard template={template} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TemplateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No templates found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {templatesError ? 
                    'You may not have permission to view templates' :
                    (searchTerm || selectedCategory !== 'all' 
                      ? 'Try adjusting your search or filters'
                      : 'No templates are available at this time')
                  }
                </Typography>
              </Box>
            )}
          </Box>
        ) : selectedOption === 'duplicate' ? (
          // Project selection for duplication
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose a Project to Duplicate
            </Typography>
            
            {/* Search */}
            <TextField
              fullWidth
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 3 }}
            />

            {/* Error Alert */}
            {projectsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {projectsError}
              </Alert>
            )}

            {/* Projects Grid */}
            {projectsLoading ? (
              <LoadingGrid />
            ) : filteredProjects.length > 0 ? (
              <Grid container spacing={2}>
                {filteredProjects.map((project) => (
                  <Grid item xs={12} sm={6} md={4} key={project._id}>
                    <ProjectCard project={project} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CopyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No projects found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm 
                    ? 'Try adjusting your search term'
                    : 'Create some projects first to enable duplication'
                  }
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      {selectedOption && selectedOption !== 'scratch' && (
        <DialogActions>
          <Button onClick={() => setSelectedOption(null)}>
            Back
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ProjectCreationOptions;